import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import type { User } from '@/types';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface UserCacheConfig {
  ttl: number; // Time to live in milliseconds
  maxSize: number; // Maximum number of entries
  enableRealtime: boolean; // Enable real-time updates
}

const DEFAULT_CONFIG: UserCacheConfig = {
  ttl: 5 * 60 * 1000, // 5 minutes
  maxSize: 1000,
  enableRealtime: true
};

export function useUserCache(config: Partial<UserCacheConfig> = {}) {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const [cache, setCache] = useState<Map<string, CacheEntry<User>>>(new Map());
  const [allUsersCache, setAllUsersCache] = useState<CacheEntry<User[]> | null>(null);
  const [loading, setLoading] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Clean expired entries
  const cleanExpiredEntries = useCallback(() => {
    const now = Date.now();
    setCache(prevCache => {
      const newCache = new Map(prevCache);
      for (const [key, entry] of newCache.entries()) {
        if (entry.expiresAt < now) {
          newCache.delete(key);
        }
      }
      return newCache;
    });

    // Clean all users cache if expired
    if (allUsersCache && allUsersCache.expiresAt < now) {
      setAllUsersCache(null);
    }
  }, [allUsersCache]);

  // Get single user from cache or fetch
  const getUser = useCallback(async (userId: string): Promise<User | null> => {
    cleanExpiredEntries();
    
    const cached = cache.get(userId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }

    // If not in cache, try to get from allUsers cache first
    if (allUsersCache && allUsersCache.expiresAt > Date.now()) {
      const user = allUsersCache.data.find(u => u.id === userId);
      if (user) {
        // Cache the individual user
        const now = Date.now();
        setCache(prev => new Map(prev).set(userId, {
          data: user,
          timestamp: now,
          expiresAt: now + finalConfig.ttl
        }));
        return user;
      }
    }

    // Fetch from database as last resort
    try {
      const userDoc = await getDocs(query(collection(db, 'users'), where('id', '==', userId)));
      if (!userDoc.empty) {
        const userData = userDoc.docs[0].data() as User;
        const now = Date.now();
        setCache(prev => new Map(prev).set(userId, {
          data: userData,
          timestamp: now,
          expiresAt: now + finalConfig.ttl
        }));
        return userData;
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }

    return null;
  }, [cache, allUsersCache, finalConfig.ttl, cleanExpiredEntries]);

  // Get all users with caching
  const getAllUsers = useCallback(async (forceRefresh = false): Promise<User[]> => {
    cleanExpiredEntries();

    if (!forceRefresh && allUsersCache && allUsersCache.expiresAt > Date.now()) {
      return allUsersCache.data;
    }

    try {
      setLoading(true);
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          roles: Array.isArray(data.roles) ? data.roles.map((role: any) => ({
            ...role,
            assignedAt: role.assignedAt?.toDate() || new Date()
          })) : []
        } as User;
      });

      const now = Date.now();
      setAllUsersCache({
        data: users,
        timestamp: now,
        expiresAt: now + finalConfig.ttl
      });

      // Also cache individual users
      const newCache = new Map(cache);
      users.forEach(user => {
        newCache.set(user.id, {
          data: user,
          timestamp: now,
          expiresAt: now + finalConfig.ttl
        });
      });
      setCache(newCache);

      return users;
    } catch (error) {
      console.error('Error fetching all users:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [allUsersCache, cache, finalConfig.ttl, cleanExpiredEntries]);

  // Get users by role with caching
  const getUsersByRole = useCallback(async (role: string): Promise<User[]> => {
    const allUsers = await getAllUsers();
    return allUsers.filter(user => 
      user.roles?.some(r => r.role === role && r.isActive)
    );
  }, [getAllUsers]);

  // Get multiple users efficiently
  const getUsers = useCallback(async (userIds: string[]): Promise<User[]> => {
    const users: User[] = [];
    const uncachedIds: string[] = [];

    // Check cache first
    for (const id of userIds) {
      const cached = cache.get(id);
      if (cached && cached.expiresAt > Date.now()) {
        users.push(cached.data);
      } else {
        uncachedIds.push(id);
      }
    }

    // Fetch uncached users
    if (uncachedIds.length > 0) {
      // Try from allUsers cache first
      if (allUsersCache && allUsersCache.expiresAt > Date.now()) {
        const cachedUsers = allUsersCache.data.filter(u => uncachedIds.includes(u.id));
        users.push(...cachedUsers);
        
        // Update individual cache
        const now = Date.now();
        setCache(prev => {
          const newCache = new Map(prev);
          cachedUsers.forEach(user => {
            newCache.set(user.id, {
              data: user,
              timestamp: now,
              expiresAt: now + finalConfig.ttl
            });
          });
          return newCache;
        });
      } else {
        // Fetch all users if we need many uncached ones
        const allUsers = await getAllUsers();
        const foundUsers = allUsers.filter(u => uncachedIds.includes(u.id));
        users.push(...foundUsers);
      }
    }

    return users;
  }, [cache, allUsersCache, getAllUsers, finalConfig.ttl]);

  // Set up real-time updates
  useEffect(() => {
    if (!finalConfig.enableRealtime) return;

    const usersQuery = query(collection(db, 'users'));
    unsubscribeRef.current = onSnapshot(usersQuery, (snapshot) => {
      const changes = snapshot.docChanges();
      if (changes.length === 0) return;

      const now = Date.now();
      
      setCache(prevCache => {
        const newCache = new Map(prevCache);
        
        changes.forEach(change => {
          const userData = {
            id: change.doc.id,
            ...change.doc.data(),
            createdAt: change.doc.data().createdAt?.toDate() || new Date(),
            updatedAt: change.doc.data().updatedAt?.toDate() || new Date(),
            roles: Array.isArray(change.doc.data().roles) ? change.doc.data().roles.map((role: any) => ({
              ...role,
              assignedAt: role.assignedAt?.toDate() || new Date()
            })) : []
          } as User;

          if (change.type === 'removed') {
            newCache.delete(change.doc.id);
          } else {
            newCache.set(change.doc.id, {
              data: userData,
              timestamp: now,
              expiresAt: now + finalConfig.ttl
            });
          }
        });

        return newCache;
      });

      // Invalidate allUsers cache on any change
      setAllUsersCache(null);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [finalConfig.enableRealtime, finalConfig.ttl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Clear cache manually
  const clearCache = useCallback(() => {
    setCache(new Map());
    setAllUsersCache(null);
  }, []);

  // Get cache stats
  const getCacheStats = useCallback(() => {
    const now = Date.now();
    const validEntries = Array.from(cache.values()).filter(entry => entry.expiresAt > now);
    
    return {
      totalEntries: cache.size,
      validEntries: validEntries.length,
      expiredEntries: cache.size - validEntries.length,
      allUsersCached: allUsersCache && allUsersCache.expiresAt > now,
      allUsersExpiry: allUsersCache?.expiresAt
    };
  }, [cache, allUsersCache]);

  return {
    getUser,
    getAllUsers,
    getUsersByRole,
    getUsers,
    clearCache,
    getCacheStats,
    loading,
    cacheSize: cache.size
  };
}
